import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Queue from "./pages/Queue";
import Potentials  from "./pages/Potentials";
import Tickets from "./pages/Tickets";
import Chat from "./pages/Chat";
import Attendants from "./pages/Attendants";
import Reports from "./pages/Reports";
import Config from "./pages/Config";
import "./App.css";
import QuickReplies from "./pages/QuickReplies";
import InternalChat from "./pages/InternalChat";
import ProcedureLibrary from "./pages/ProcedureLibrary";
import { NotificationSystem } from "./components/NotificationSystem";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  if (user.role !== "admin") return <Navigate to="/" />;

  return <Layout>{children}</Layout>;
};

const AnalysisRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  if (!["analyst", "admin"].includes(user.role)) return <Navigate to="/" />;

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? <Navigate to="/" /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <PrivateRoute>
            <Queue />
          </PrivateRoute>
        }
      />
      <Route
        path="/potentials"
        element={
          <PrivateRoute>
            <Potentials />
          </PrivateRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <PrivateRoute>
            <Tickets />
          </PrivateRoute>
        }
      />
      <Route
        path="/procedures"
        element={
          <PrivateRoute>
            <ProcedureLibrary />
          </PrivateRoute>
        }
      />
      <Route
        path="/chat/:ticketId?"
        element={
          <PrivateRoute>
            <Chat />
          </PrivateRoute>
        }
      />
      <Route
        path="/quick-replies"
        element={
          <PrivateRoute>
            <QuickReplies />
          </PrivateRoute>
        }
      />
      <Route
        path="/internal-chat"
        element={
          <PrivateRoute>
            <InternalChat />
          </PrivateRoute>
        }
      />
      <Route
        path="/attendants"
        element={
          <PrivateRoute>
            <Attendants />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <AnalysisRoute>
            <Reports />
          </AnalysisRoute>
        }
      />
      <Route
        path="/config"
        element={
          <AdminRoute>
            <Config />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster />
        <NotificationSystem />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
